import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import CommitProgress from "../components/CommitProgress";
import LockOutlineIcon from "@mui/icons-material/LockOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { SHOW_D365_COMMIT_ACTIONS } from "../config/featureFlags";
import { SalesOrderStatusD365 } from "../hooks/SalesOrder/D365/useGetSalesOrderHeaderStatusD365";
import {
    createAxiosClient,
    getResolvedRedirectBaseUrl,
} from "@ntg/component-library/utilities";
import { getLineStatusChipStyle } from "../explorer/nodes/lineStatusColors";
import { useLocation, useNavigate } from "react-router";
import { useTheme } from "@mui/material/styles";

interface ISalesOrderToolbarProps {
    D365Status: SalesOrderStatusD365;
    OrderID: number;
}

const postCommitSalesOrder = async (orderID: number): Promise<string> => {
    const axiosAuth = createAxiosClient("D365FO");
    const response = await axiosAuth.post<string>(
        "/FunctionAppsStarter/CommitSalesOrder",
        orderID,
        {
            headers: {
                "Content-Type": "application/json-patch+json",
                Accept: "*/*",
            },
        },
    );
    return response.data;
};

export default function SalesOrderToolbar(props: ISalesOrderToolbarProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const [instanceId, setInstanceId] = useState<string | null>(null);

    const openCSSalesOrder = () => {
        const baseUrl = getResolvedRedirectBaseUrl();
        window.open(
            `${baseUrl}SOrder/SOrder.aspx?SOrderID=${props.OrderID}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    const handleCommit = async () => {
        try {
            const instanceId = await postCommitSalesOrder(props.OrderID);
            navigate(`${location.pathname}/commit/${instanceId}`);
        } catch (error) {
            console.error(error);
            alert("Failed to start workflow");
        }
    };

    return (
        <div
            style={{
                padding: "9px 3px 1px 3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
            }}
        >
            <Chip
                label={props.D365Status}
                size="small"
                sx={{
                    fontWeight: 600,
                    ...getLineStatusChipStyle(props.D365Status, isDark),
                }}
            />

            <ButtonGroup
                variant="contained"
                size="small"
                color="primary"
                aria-label="Basic button group"
                sx={{ marginLeft: "auto" }}
            >
                {/* AB#12733: D365 commit/confirm action hidden for now — see featureFlags.ts */}
                {SHOW_D365_COMMIT_ACTIONS && (
                    <Tooltip
                        title={
                            props.D365Status === "Invoiced"
                                ? "Cannot modify once Sales Order is Invoiced"
                                : ""
                        }
                    >
                        <span>
                            <Button
                                onClick={handleCommit}
                                id="syncButton"
                                disabled={
                                    props.D365Status === "Loading" ||
                                    props.D365Status !== "None"
                                }
                                endIcon={
                                    props.D365Status === "Loading" ||
                                    props.D365Status === "Invoiced" ? (
                                        <LockOutlineIcon />
                                    ) : undefined
                                }
                            >
                                {props.D365Status === "None" ? "Confirm" : "Update"}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip
                    title={
                        props.D365Status === "Invoiced"
                            ? "Cannot delete once Sales Order is Invoiced"
                            : ""
                    }
                >
                    <span>
                        <Button
                            id="deleteButton"
                            disabled={
                                props.D365Status === "Loading" ||
                                props.D365Status === "Invoiced"
                            }
                            endIcon={
                                props.D365Status === "Loading" ||
                                props.D365Status === "Invoiced" ? (
                                    <LockOutlineIcon />
                                ) : undefined
                            }
                        >
                            Delete
                        </Button>
                    </span>
                </Tooltip>
            </ButtonGroup>
            <ButtonGroup
                variant="contained"
                size="small"
                color="info"
                aria-label="Explorer button group"
            >
                <Button
                    onClick={() => {
                        navigate(`/explorer/SalesOrder/${props.OrderID}`);
                    }}
                    endIcon={<AccountTreeIcon />}
                >
                    Open in Explorer
                </Button>
            </ButtonGroup>

            <ButtonGroup
                variant="contained"
                size="small"
                color="warning"
                aria-label="Basic button group"
            >
                <Button
                    onClick={openCSSalesOrder}
                    endIcon={
                        <OpenInBrowserIcon htmlColor="inherit !important" />
                    }
                >
                    CS Sales Order
                </Button>
            </ButtonGroup>
            {instanceId && <CommitProgress />}
        </div>
    );
}
